import type { AdvanceSessionResponse } from '@/api/types'
import type { FlowState, StepPosition } from './flow-state'

/**
 * The pure decisions a session-aware flow makes (build plan Track F, F2; docs/decisions.md D-57).
 * Nothing here performs I/O - every function takes what a response or a state already carries and
 * answers what comes next, which is what makes the two together the whole of "the sequence itself is
 * never client state" (design §4): the array of steps never lives in the browser, and neither of
 * these functions ever sees one.
 */

/** What `resolving-step` (or a mid-session reload landing there) does with an advance response. */
export type ResolvedStep =
  | { readonly kind: 'next-step'; readonly shortCode: string; readonly position: StepPosition }
  | { readonly kind: 'handoff'; readonly position: StepPosition; readonly redirectUrl: string }
  /**
   * The current step IS the external hand-off and its destination has not been stored yet. Not the
   * same answer as `unusable`, and the difference is the whole of it: `unusable` says there is
   * nowhere to send this patient, and this says not yet.
   */
  | { readonly kind: 'handoff-pending'; readonly position: StepPosition }
  | { readonly kind: 'complete' }
  | { readonly kind: 'unusable' }

/**
 * Turns the server's answer to "what is current" into one of the five things `resolving-step` can
 * do next. A `NextStep` outcome with no short code or no position is treated as `unusable` rather
 * than trusted partially - a malformed response is not a step this app can act on.
 */
export function resolveAdvanceResponse(response: AdvanceSessionResponse): ResolvedStep {
  switch (response.outcome) {
    case 'NextStep':
      return response.shortCode && response.position
        ? { kind: 'next-step', shortCode: response.shortCode, position: response.position }
        : { kind: 'unusable' }
    case 'Handoff':
      // NO POSITION is malformed, exactly as a NextStep with no short code above is, and is not a
      // step this app can act on.
      //
      // No redirectUrl is a different thing, and used to be answered as though it were the same. The
      // redirect is stored by a peer that only learns the patient is here once they OPEN the link -
      // through an outbox drained on a thirty-second poll, then a message, then a call to a third
      // party - so for the first seconds of a session the true answer is "not yet", and reading it
      // as "never" dead-links a patient whose destination is moments away. `handoff-pending` is that
      // distinction; `scan-flow.tsx` holds on it, bounded, and falls through to `unusable` if the
      // redirect genuinely never comes.
      if (!response.position) {
        return { kind: 'unusable' }
      }

      return response.redirectUrl
        ? { kind: 'handoff', position: response.position, redirectUrl: response.redirectUrl }
        : { kind: 'handoff-pending', position: response.position }
    case 'Complete':
      return { kind: 'complete' }
    case 'Unusable':
    default:
      return { kind: 'unusable' }
  }
}

/**
 * What happens after one step reaches an outcome the BACKEND treats as terminal for that
 * assessment: `complete` (`AssessmentState.Completed`) or `thanked` (the two-strike
 * `AssessmentState.Failed`). Outside a session - no `position`, because a single-assessment link
 * never carries one - the state is returned unchanged: this is the whole of the "unchanged in shape"
 * promise for a link that predates this feature.
 *
 * Inside a session, the current screen stays exactly where it is when `position.isLast` - there is
 * nothing after it, whether because the session is out of steps or because what comes next is the
 * external hand-off already reflected by THIS position not being last - and moves to `advancing`
 * otherwise, carrying the session token forward so `resolving-step` can ask what is next.
 *
 * DELIBERATELY NOT `scan-failed`. A first failed attempt (`ReportScanFailureCommand`'s live branch)
 * leaves the assessment `Issued`/`Opened`/`Started` - not terminal - so `AdvanceSessionCommand` would
 * only reissue a grant for the SAME step, not move to the next one. Auto-advancing away from this
 * screen would cost a network round trip and the screen's own *Try again* affordance for nothing:
 * the patient would land right back on the same step through `loading-config`, having lost the
 * in-place retry. `scan-failed` keeps its existing behaviour, in a session or not.
 */
export function afterStepOutcome(state: FlowState, sessionToken: string | null): FlowState {
  if (state.step !== 'complete' && state.step !== 'thanked') {
    return state
  }

  if (sessionToken === null || state.position === undefined || state.position.isLast) {
    return state
  }

  return { step: 'advancing', sessionToken }
}
